/**
 * The Plant time fields, as the settings page and the onboarding step edit them: the plant's zone (a searchable list
 * of the browser's zones), when its production day starts (a time, and whether the day starts the evening before and
 * counts toward the next date), its week start, and its shift calendar (a row per shift: its name and its start, in
 * the plant's day order, and optionally when its work ends, by which the gateways fetch updates). Controlled and pure:
 * the form comes in and every edit goes out through onChange. The words are lib/plant-time-form's.
 */
import { useId } from "react";
import { Plus, Trash2 } from "lucide-react";
import { AutocompleteInput } from "@/components/shared/AutocompleteInput";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Label } from "@/lib/ui/label";
import { NativeSelect } from "@/lib/ui/native-select";
import {
  WEEKDAYS,
  dayWords,
  eveningWords,
  newShiftKey,
  todaysShifts,
  zoneSuggestionWords,
  type PlantTimeForm,
  type ShiftRow,
} from "@/lib/plant-time-form";

export interface PlantTimeFieldsProps {
  form: PlantTimeForm;
  onChange: (next: PlantTimeForm) => void;
  /** The zones the zone choice offers (timeZoneOptions). */
  zones: string[];
  /** Read-only: a member reads the plant time; an org admin sets it. */
  disabled?: boolean;
  /** Leave out the week start (onboarding asks only for the zone, the day start and the shifts). */
  hideWeekStart?: boolean;
  /**
   * The browser's zone, offered while the form has no zone: a suggestion the person confirms with the Use button, never
   * filled in (a laptop away from the plant names the wrong zone, and imports read their times on it).
   */
  suggestedZone?: string;
}

export function PlantTimeFields({
  form,
  onChange,
  zones,
  disabled = false,
  hideWeekStart = false,
  suggestedZone,
}: PlantTimeFieldsProps) {
  const id = useId();
  const set = (patch: Partial<PlantTimeForm>) => onChange({ ...form, ...patch });
  const setShift = (key: string, patch: Partial<ShiftRow>) =>
    set({ shifts: form.shifts.map((s) => (s.key === key ? { ...s, ...patch } : s)) });
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-zone`}>Time zone</Label>
        <AutocompleteInput
          id={`${id}-zone`}
          value={form.zone}
          onChange={(zone) => set({ zone })}
          suggestions={zones}
          max={12}
          disabled={disabled}
          placeholder="Search for a zone, like America/New_York"
          aria-describedby={`${id}-zone-hint`}
        />
        <p id={`${id}-zone-hint`} className="text-xs text-on-surface-muted">
          The zone the plant&apos;s clocks read. Every day, shift and week is cut on it.
        </p>
        {!disabled && suggestedZone && !form.zone.trim() && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-on-surface-muted">
            <span>{zoneSuggestionWords(suggestedZone)}</span>
            <Button type="button" variant="outline" size="sm" onClick={() => set({ zone: suggestedZone })}>
              Use {suggestedZone}
            </Button>
          </div>
        )}
      </div>

      <fieldset className="flex flex-col gap-1.5" disabled={disabled}>
        <legend className="mb-1.5 text-sm font-medium text-on-surface">The production day starts at</legend>
        <Input
          id={`${id}-day-start`}
          type="time"
          aria-label="The production day starts at"
          className="w-36"
          value={form.dayStart}
          onChange={(e) => set({ dayStart: e.target.value })}
        />
        <label className="mt-1 flex items-start gap-2 text-sm text-on-surface">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-brand disabled:cursor-not-allowed"
            checked={form.eveningBefore}
            onChange={(e) => set({ eveningBefore: e.target.checked })}
          />
          <span>
            The day starts the evening before and counts toward the next date.
            <span className="block text-xs text-on-surface-muted">{eveningWords(form.eveningBefore ? form.dayStart : "22:30")}.</span>
          </span>
        </label>
      </fieldset>

      {!hideWeekStart && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-week`}>The plant week starts on</Label>
          <NativeSelect
            id={`${id}-week`}
            className="w-56"
            disabled={disabled}
            value={form.weekStart === null ? "" : String(form.weekStart)}
            onChange={(e) => set({ weekStart: e.target.value === "" ? null : Number(e.target.value) })}
          >
            <option value="">Not chosen: Monday</option>
            {WEEKDAYS.map((d) => (
              <option key={d.value} value={String(d.value)}>
                {d.label}
              </option>
            ))}
          </NativeSelect>
        </div>
      )}

      <fieldset className="flex flex-col gap-2" disabled={disabled}>
        <legend className="mb-1 text-sm font-medium text-on-surface">Shift calendar</legend>
        <p className="text-xs text-on-surface-muted">
          Each shift runs from its start to the next shift&apos;s start; the last one runs over midnight. With no shifts,
          nothing is split by shift. A shift&apos;s end is when its work stops: the gateways fetch updates by it, and it
          never moves where a shift is cut.
        </p>
        {form.shifts.length > 0 && (
          <ul className="flex flex-col gap-2">
            {form.shifts.map((s, i) => (
              <li key={s.key} className="flex flex-wrap items-center gap-2">
                <Input
                  aria-label={`Shift ${i + 1} name`}
                  className="w-40"
                  placeholder="Name, like Night or 1"
                  value={s.label}
                  onChange={(e) => setShift(s.key, { label: e.target.value })}
                />
                <Input
                  aria-label={`Shift ${i + 1} starts at`}
                  type="time"
                  className="w-36"
                  value={s.start}
                  onChange={(e) => setShift(s.key, { start: e.target.value })}
                />
                <span className="text-xs text-on-surface-muted">ends</span>
                <Input
                  aria-label={`Shift ${i + 1} ends at`}
                  type="time"
                  className="w-36"
                  value={s.end ?? ""}
                  onChange={(e) => setShift(s.key, { end: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove shift ${s.label.trim() || i + 1}`}
                  onClick={() => set({ shifts: form.shifts.filter((x) => x.key !== s.key) })}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => set({ shifts: [...form.shifts, { key: newShiftKey(), label: "", start: "" }] })}
          >
            <Plus className="size-4" aria-hidden />
            Add a shift
          </Button>
        </div>
      </fieldset>
    </div>
  );
}

/** Today's shifts on the plant's clock as the form has it, with each one's start and end. */
export function TodaysShiftsPreview({ form, now }: { form: PlantTimeForm; now: Date }) {
  const today = todaysShifts(form, now);
  return (
    <section aria-label="Today's shifts" className="rounded-md border border-border bg-surface-muted/40 p-3 text-sm">
      {!today ? (
        <p className="text-on-surface-muted">Choose a zone and when the day starts to see today&apos;s shifts.</p>
      ) : (
        <>
          <p className="text-on-surface">
            Today on the plant&apos;s clock is {dayWords(today.day)}, from {today.from} to {today.to}.
          </p>
          {today.rows.length === 0 ? (
            <p className="mt-1 text-on-surface-muted">No shift calendar: nothing is split by shift.</p>
          ) : (
            <ul className="mt-2 flex flex-col gap-1">
              {today.rows.map((r) => (
                <li key={`${r.label}-${r.from}`} className="text-on-surface">
                  <span className="font-medium">{r.label}</span>: {r.from} to {r.to}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
